import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type { RestDay, ShiftsRow } from 'modules/shift_management/lib/types'
import restDaysCollection from 'modules/shift_management/store/api/collections/restDays'

import { trackDeleteRestDaysEvent } from '../lib/trackDeleteEvent'
import { useSelectionDispatch } from './useSelection'
import { useShiftsRows } from './useShiftsRows'

const useDestroyRestDay = (restDay: RestDay) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const { refreshStats } = useRefreshStats(view.startAt, view.endAt)
  const restDaysIds = [restDay.id]

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      dispatchShiftsRows({ type: 'DESTROY_REST_DAY', restDay })
      trackDeleteRestDaysEvent([restDay], 1, shiftsConflicts, 'contextual_button')

      await restDaysCollection.rpc('bulk_destroy', { ids: restDaysIds })
      await refreshStats(shiftsRows)

      closePortal()
    },
    {
      successMessage: i18n.t('shift_management.actions.delete_rest_day_success'),
    }
  )

  return {
    onDestroy: performAction,
    submitting,
  }
}

const useDestroyRestDays = (restDays: RestDay[], employeeCount: number) => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const dispatchSelection = useSelectionDispatch()
  const [{ view, shiftsConflicts }] = useSchedule()
  const { refreshStats } = useRefreshStats(view.startAt, view.endAt)

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      trackDeleteRestDaysEvent(restDays, employeeCount, shiftsConflicts, 'top_bar_button')

      const restDayIds = restDays.map((restDay) => String(restDay.id))
      await restDaysCollection.rpc('bulk_destroy', { ids: restDayIds })

      // Get shiftsRows where there are affected shifts to update stats
      const restDayKeys = new Set(restDays.map((restDay) => String(restDay.employeeId)))
      const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => restDayKeys.has(row.key))
      await refreshStats(previousShiftsRows)

      dispatchShiftsRows({ type: 'DESTROY_REST_DAYS', restDays })
      dispatchSelection({ type: 'CLEAR_SELECTION' })
      closePortal()
    },
    {
      successMessage: i18n.t('shift_management.actions.delete_rest_day_success'),
    }
  )

  return {
    onDestroy: performAction,
    submitting,
  }
}

export { useDestroyRestDay, useDestroyRestDays }
